import React from 'react';
import { motion } from 'framer-motion';
import {
  DevicePhoneMobileIcon,
  UserPlusIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  CircleStackIcon,
  EnvelopeIcon,
  KeyIcon,
  ArrowRightIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const OptIn2FA: React.FC = () => {
  const steps = [
    {
      number: 1,
      title: 'Download and Install',
      description: 'The user downloads the mobile app from the App Store and installs it.',
      icon: DevicePhoneMobileIcon,
    },
    {
      number: 2,
      title: 'Start Registration',
      description: 'The user starts the registration in the mobile app. They are prompted to enter their phone number and password.',
      icon: UserPlusIcon,
    },
    {
      number: 3,
      title: 'Consent Message Display',
      description: 'Below the input field, a message requesting for their consent is displayed: "Click verify to receive a one-time verification code on your phone."',
      icon: ChatBubbleLeftRightIcon,
    },
    {
      number: 4,
      title: 'User Clicks Verify',
      description: 'The user clicks on "Verify" to receive the one-time verification code on their mobile phone.',
      icon: CheckCircleIcon,
    },
    {
      number: 5,
      title: 'Database Update',
      description: 'User data is updated in the database to indicate that they opted-in to receive a one-time text message with code for phone number verification. The database record will serve as proof of opt-in.',
      icon: CircleStackIcon,
    },
    {
      number: 6,
      title: 'Receive Verification Code',
      description: 'The user receives a text message with a verification code.',
      icon: DevicePhoneMobileIcon,
    },
    {
      number: 7,
      title: 'Enter Verification Code',
      description: 'The user enters the verification code.',
      icon: KeyIcon,
    },
    {
      number: 8,
      title: 'Proceed with Valid Code',
      description: 'If the verification code is valid, user registration is completed and user is logged in.',
      icon: ArrowRightIcon,
    },
    {
      number: 9,
      title: 'Reenter with Invalid Code',
      description: 'If the user enters an invalid code, share a message with the user that the code is invalid and ask them to try again.',
      icon: XCircleIcon,
    },
    {
      number: 10,
      title: 'User Does Not Click Verify and Clicks Register',
      description: 'If the user does not click on "Verify" and clicks "Register", user registration is completed and user is logged in.',
      icon: XCircleIcon,
    },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-secondary-50 section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">
              Prompt Shots
            </h1>
            <h3 className="text-xl md:text-2xl font-semibold text-primary-600 mb-6">
              Opt-in for 2FA during User Registration
            </h3>
            <p className="text-md text-secondary-600">
              This is a step-by-step opt-in process for 2FA during user registration on the mobile app.
              It ensures that the users provide explicit consent before receiving 2FA verification codes via SMS on their mobile phone.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="bg-white section-padding">
        <div className="container-custom">
          <div className="space-y-8">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="flex gap-6"
              >
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-primary-600" />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-md font-bold text-primary-600">
                      {step.number}.
                    </span>
                    <h3 className="text-md font-bold text-secondary-600">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-md text-secondary-600">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Summary Section */}
      <section className="bg-secondary-50 section-padding">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white rounded-xl p-8 shadow-lg"
          >
            <h3 className="text-xl md:text-2xl font-bold text-secondary-900 mb-4">
              Outline
            </h3>
            <ul className="space-y-2 text-md text-secondary-600">
              <li className="flex items-start">
                <div className="w-2 h-2 bg-primary-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                <span>Users are explicitly informed about receiving a one-time verification code by text</span>
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 bg-primary-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                <span>Consent is obtained during user registration before an SMS is sent</span>
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 bg-primary-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                <span>Opt-in status is recorded in the database for compliance purposes</span>
              </li>
              <li className="flex items-start">
                <div className="w-2 h-2 bg-primary-500 rounded-full mr-3 mt-2 flex-shrink-0"></div>
                <span>User can proceed with registration if they do not want to receive a one-time verification code</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OptIn2FA;
